import type { HotStackUiPhase } from '@frontend/types/hotloading';
import { stackPhaseFromServer, type RuntimeModeResponse } from '@frontend/api-clients/runtimeModeClient';

export type RouteMode = 'hot' | 'parity';

export interface HotloadingUiState {
  runtimeEnvironment: 'testing' | 'staging' | 'production';
  showHotloadingControl: boolean;
  stackUiPhase: HotStackUiPhase;
  routeMode: RouteMode;
  routeToggleEnabled: boolean;
  routeToHotAllowed: boolean;
  hotloadingError: string;
}

export interface ApplyRuntimePayloadOptions {
  preserveTransitional?: boolean;
  routeOnly?: boolean;
  currentStackUiPhase?: HotStackUiPhase;
  /** Clear a prior operator error (successful action). Status polls must omit this. */
  clearHotloadingError?: boolean;
}

export function resolveRouteToggleDisabled(props: {
  routeToggleEnabled: boolean;
  routeBusy: boolean;
  stackPhase: HotStackUiPhase;
}): boolean {
  const stackTransition = props.stackPhase === 'loading' || props.stackPhase === 'unloading';
  return !props.routeToggleEnabled || props.routeBusy || stackTransition;
}

/** Block stack down while Caddy still routes to hot (parity first avoids 502). */
export function resolveStackToggleDisabled(props: {
  routeMode: RouteMode;
  stackPhase: HotStackUiPhase;
  stackBusy: boolean;
  routeBusy: boolean;
}): boolean {
  const stackTransition = props.stackPhase === 'loading' || props.stackPhase === 'unloading';
  const stackUp = props.stackPhase === 'up';
  const routeBlocksStackDown = props.routeMode === 'hot' && stackUp;
  return props.stackBusy || props.routeBusy || stackTransition || routeBlocksStackDown;
}

export function canToggleStack(props: {
  routeMode: RouteMode;
  stackUiPhase: HotStackUiPhase;
}): boolean {
  if (props.stackUiPhase !== 'up') {
    return true;
  }
  return props.routeMode !== 'hot';
}

export function canSwitchRouteToHot(routeToHotAllowed: boolean, nextMode: RouteMode): boolean {
  if (nextMode !== 'hot') {
    return true;
  }
  return routeToHotAllowed;
}

/**
 * Stack badge click should POST hotStandby (bring/refresh up) when down or up-but-unhealthy.
 * Healthy up still toggles down to parityIdle.
 */
export function stackClickTargetsRefresh(props: {
  stackUiPhase: HotStackUiPhase;
  routeToHotAllowed: boolean;
}): boolean {
  if (props.stackUiPhase === 'down') {
    return true;
  }
  if (props.stackUiPhase === 'up' && !props.routeToHotAllowed) {
    return true;
  }
  return false;
}

/** Badge label follows hotloadState first (Caddy read can lag behind orchestration). */
export function routeModeFromRuntimePayload(data: RuntimeModeResponse): RouteMode {
  if (data.hotloadState === 'hotRouted') {
    return 'hot';
  }
  if (data.hotloadState === 'parityIdle' || data.hotloadState === 'hotStandby') {
    return 'parity';
  }
  return data.routeMode === 'hot' || data.mode === 'hot' ? 'hot' : 'parity';
}

export function applyRuntimePayloadState(
  prev: HotloadingUiState,
  data: RuntimeModeResponse,
  options: ApplyRuntimePayloadOptions = {}
): HotloadingUiState {
  const { preserveTransitional = false, routeOnly = false, currentStackUiPhase = prev.stackUiPhase } = options;

  const env =
    data.environment === 'testing' || data.environment === 'staging' || data.environment === 'production'
      ? data.environment
      : 'production';

  const nextRoute = routeModeFromRuntimePayload(data);
  const eligible = Boolean(data.hotloadingControlEligible);

  let hotloadingError = prev.hotloadingError;
  if (!eligible && data.message) {
    hotloadingError = data.message;
  } else if (options.clearHotloadingError) {
    // Successful operator action or explicit dismiss — do not clear on status polls
    // (eligible alone used to wipe API errors before the user could read them).
    hotloadingError = '';
  }

  const base: HotloadingUiState = {
    ...prev,
    runtimeEnvironment: env,
    routeMode: nextRoute,
    routeToggleEnabled: Boolean(data.routeToggleEnabled),
    routeToHotAllowed: Boolean(data.routeToHotAllowed),
    showHotloadingControl: env === 'testing',
    hotloadingError
  };

  if (routeOnly) {
    return base;
  }

  const serverStack = stackPhaseFromServer(data);
  if (!preserveTransitional || (currentStackUiPhase !== 'loading' && currentStackUiPhase !== 'unloading')) {
    return { ...base, stackUiPhase: serverStack };
  }
  if (serverStack === 'up' && currentStackUiPhase === 'loading') {
    return { ...base, stackUiPhase: 'up' };
  }
  if (serverStack === 'down' && currentStackUiPhase === 'unloading') {
    return { ...base, stackUiPhase: 'down' };
  }
  return { ...base, stackUiPhase: currentStackUiPhase };
}
